"use client";

import { useEffect, useRef, useState } from "react";

function useEnterAnimation(ref: React.RefObject<HTMLElement | null>) {
  const [anim, setAnim] = useState<"idle" | "pending" | "in">("idle");
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnim("in");
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    setAnim("pending");
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return anim;
}

interface BenchmarkChart {
  title: string;
  /** One score per model, in the same order as `models`. `null` = n/a. */
  values: (number | null)[];
  /** Optional labels that preserve reported precision or text such as "tied". */
  displayValues?: string[];
}

interface BenchmarkChartsProps {
  /** Full model names, used in tooltips and the screen-reader table. */
  models: string[];
  /** Short names shown under the bars; defaults to `models`. */
  labels?: string[];
  charts: BenchmarkChart[];
  /** Index into `models` of the bar to highlight. */
  highlight?: number;
  /** Metric name, e.g. "F1@0.5" — shown in tooltips and the sr table. */
  unit?: string;
  /** Upper bound of the scale all bars share. */
  max?: number;
}

interface BenchmarkBarGroup {
  label: string;
  values: number[];
}

interface BenchmarkGroupedBarsProps {
  title: string;
  series: string[];
  groups: BenchmarkBarGroup[];
  max: number;
  ticks: number[];
  referenceValue: number;
  referenceLabel: string;
  caption: string;
  unit?: string;
}

/**
 * Small-multiple vertical bar charts for benchmark scores. Usage in MDX:
 *   <BenchmarkCharts
 *     unit="F1@0.5"
 *     models={["Moondream 3.1 9B", "Qwen3.5 9B", ...]}
 *     labels={["Moondream 3.1", "Qwen3.5", ...]}
 *     charts={[{ title: "COCO (val)", values: [81.46, 75.58, ...] }, ...]}
 *   />
 */
export function BenchmarkCharts({
  models,
  labels,
  charts,
  highlight = 0,
  unit,
  max = 100,
}: BenchmarkChartsProps) {
  const ref = useRef<HTMLElement>(null);
  const anim = useEnterAnimation(ref);
  const names = labels ?? models;

  return (
    <figure className="blog-evals" data-count={charts.length} data-anim={anim} ref={ref}>
      <div className="blog-evals-grid" aria-hidden="true">
        {charts.map((chart) => {
          const best = Math.max(...chart.values.filter((v): v is number => v !== null));
          return (
            <div className="blog-eval" key={chart.title}>
              <h4 className="blog-eval-title">{chart.title}</h4>
              <div className="blog-eval-plot">
                {chart.values.map((value, i) => (
                  <div
                    className={
                      "blog-eval-col" + (i === highlight ? " is-highlight" : "")
                    }
                    data-tip={
                      value === null
                        ? `${models[i]} · n/a`
                        : `${models[i]} · ${chart.displayValues?.[i] ?? value}${unit ? ` ${unit}` : ""}`
                    }
                    key={i}
                  >
                    <div
                      className="blog-eval-stack"
                      style={
                        {
                          "--h": value === null ? 0 : (value / max) * 100,
                          "--d": `${i * 70}ms`,
                        } as React.CSSProperties
                      }
                    >
                      <span
                        className={
                          "blog-eval-value" +
                          (value !== null && value === best ? " is-best" : "") +
                          (value === null ? " is-na" : "")
                        }
                      >
                        {chart.displayValues?.[i] ?? value ?? "n/a"}
                      </span>
                      {value !== null && <div className="blog-eval-bar" />}
                    </div>
                  </div>
                ))}
              </div>
              <div className="blog-eval-names">
                {names.map((name, i) => (
                  <div className="blog-eval-name-slot" key={i}>
                    <span
                      className={
                        "blog-eval-name" + (i === highlight ? " is-highlight" : "")
                      }
                    >
                      {name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Same data as an accessible table */}
      <table className="sr-only">
        <caption>
          Benchmark scores{unit ? ` (${unit})` : ""}; higher is better.
        </caption>
        <thead>
          <tr>
            <th scope="col">Benchmark</th>
            {models.map((m) => (
              <th scope="col" key={m}>
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {charts.map((chart) => (
            <tr key={chart.title}>
              <th scope="row">{chart.title}</th>
              {chart.values.map((v, i) => (
                <td key={i}>{chart.displayValues?.[i] ?? v ?? "n/a"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/**
 * Grouped vertical bars for comparing the same series across hardware.
 */
export function BenchmarkGroupedBars({
  title,
  series,
  groups,
  max,
  ticks,
  referenceValue,
  referenceLabel,
  caption,
  unit = "x",
}: BenchmarkGroupedBarsProps) {
  const ref = useRef<HTMLElement>(null);
  const anim = useEnterAnimation(ref);
  const height = (value: number) =>
    Math.min(100, Math.max(0, (value / max) * 100));
  const formatValue = (value: number) => `${value.toFixed(2)}${unit}`;

  return (
    <figure className="blog-grouped-bars" data-anim={anim} ref={ref}>
      <h4 className="blog-grouped-title">{title}</h4>
      <div className="blog-grouped-scroll">
        <div
          className="blog-grouped-chart"
          style={{ "--series-count": series.length } as React.CSSProperties}
          aria-hidden="true"
        >
          <div className="blog-grouped-y-axis">
            {ticks.map((tick) => (
              <span
                className="blog-grouped-y-tick"
                style={{ bottom: `${height(tick)}%` }}
                key={tick}
              >
                {tick.toFixed(1)}{unit}
              </span>
            ))}
          </div>
          <div className="blog-grouped-main">
            <div className="blog-grouped-plot">
              {ticks.map((tick) => (
                <span
                  className="blog-grouped-gridline"
                  style={{ bottom: `${height(tick)}%` }}
                  key={tick}
                />
              ))}
              <span
                className="blog-grouped-reference"
                style={{ bottom: `${height(referenceValue)}%` }}
              >
                <span>{referenceLabel}</span>
              </span>
              <div className="blog-grouped-groups">
                {groups.map((group, groupIndex) => (
                  <div className="blog-grouped-group" key={group.label}>
                    {group.values.map((value, seriesIndex) => (
                      <div
                        className="blog-grouped-col"
                        key={series[seriesIndex]}
                      >
                        <span className="blog-grouped-tooltip">
                          {group.label} · {series[seriesIndex]} · {formatValue(value)}
                        </span>
                        <div
                          className="blog-grouped-stack"
                          style={
                            {
                              "--h": height(value),
                              "--d": `${(groupIndex * series.length + seriesIndex) * 55}ms`,
                            } as React.CSSProperties
                          }
                        >
                          <span className="blog-grouped-value">
                            {formatValue(value)}
                          </span>
                          <div className="blog-grouped-bar" />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="blog-grouped-labels">
              {groups.map((group) => (
                <div className="blog-grouped-label-group" key={group.label}>
                  <div className="blog-grouped-series-labels">
                    {series.map((name) => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                  <div className="blog-grouped-group-name">{group.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <figcaption className="blog-grouped-caption">{caption}</figcaption>
      <table className="sr-only">
        <caption>
          {title}. {caption}
        </caption>
        <thead>
          <tr>
            <th scope="col">GPU</th>
            {series.map((name) => (
              <th scope="col" key={name}>
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={group.label}>
              <th scope="row">{group.label}</th>
              {group.values.map((value, index) => (
                <td key={series[index]}>{formatValue(value)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

interface ScatterPoint {
  label: string;
  x: number;
  y: number;
  /** Which side of the dot the label sits on. */
  side?: "left" | "right";
}

interface BenchmarkScatterProps {
  points: ScatterPoint[];
  /** Index into `points` of the dot to highlight. */
  highlight?: number;
  xLabel: string;
  yLabel: string;
  /** Units appended to x / y values in tooltips. */
  xUnit?: string;
  yUnit?: string;
  xMax: number;
  yMin: number;
  yMax: number;
  xTicks: number[];
  yTicks: number[];
}

/**
 * Scatter plot in the same visual family as <BenchmarkCharts>. Usage in MDX:
 *   <BenchmarkScatter
 *     xLabel="average requests per second" yLabel="average benchmark score"
 *     xMax={36} yMin={50} yMax={80} xTicks={[0, 10, 20, 30]} yTicks={[50, 60, 70, 80]}
 *     points={[{ label: "Moondream 3.1", x: 34.2, y: 76.8, side: "left" }, ...]}
 *   />
 */
export function BenchmarkScatter({
  points,
  highlight = 0,
  xLabel,
  yLabel,
  xUnit,
  yUnit,
  xMax,
  yMin,
  yMax,
  xTicks,
  yTicks,
}: BenchmarkScatterProps) {
  const ref = useRef<HTMLElement>(null);
  const anim = useEnterAnimation(ref);

  const xPct = (x: number) => (x / xMax) * 100;
  const yPct = (y: number) => ((y - yMin) / (yMax - yMin)) * 100;

  return (
    <figure className="blog-scatter" data-anim={anim} ref={ref}>
      <div aria-hidden="true">
        <div className="blog-scatter-ylabel">{yLabel}</div>
        <div className="blog-scatter-plot">
          {yTicks.map((t) => (
            <div className="blog-scatter-gridline" style={{ bottom: `${yPct(t)}%` }} key={`y${t}`}>
              <span className="blog-scatter-tick">{t}</span>
            </div>
          ))}
          {xTicks.map((t) => (
            <div className="blog-scatter-gridline-x" style={{ left: `${xPct(t)}%` }} key={`x${t}`}>
              <span className="blog-scatter-tick">{t}</span>
            </div>
          ))}
          {points.map((p, i) => (
            <div
              className={
                "blog-scatter-point" +
                (i === highlight ? " is-highlight" : "") +
                (p.side === "left" ? " is-left" : "")
              }
              style={
                {
                  left: `${xPct(p.x)}%`,
                  bottom: `${yPct(p.y)}%`,
                  "--d": `${i * 90}ms`,
                } as React.CSSProperties
              }
              data-tip={`${p.label} · ${p.x}${xUnit ? ` ${xUnit}` : ""} · ${p.y}${yUnit ? ` ${yUnit}` : ""}`}
              key={p.label}
            >
              <span className="blog-scatter-dot" />
              <span className="blog-scatter-name">{p.label}</span>
            </div>
          ))}
        </div>
        <div className="blog-scatter-xlabel">{xLabel}</div>
      </div>
      {/* Same data as an accessible table */}
      <table className="sr-only">
        <caption>
          {yLabel} versus {xLabel}.
        </caption>
        <thead>
          <tr>
            <th scope="col">Model</th>
            <th scope="col">{xLabel}</th>
            <th scope="col">{yLabel}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.label}>
              <th scope="row">{p.label}</th>
              <td>{p.x}</td>
              <td>{p.y}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
